import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MicIcon from '@mui/icons-material/Mic';
import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import { selectIsDarkMode } from '@common-reducers/UserSelectors';
import ContactAvatar from '@/extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { formatDuration, generateWaveformHeights } from './team-inbox-helpers';

const BAR_COUNT = 30;
const BAR_WIDTH = 3;
const BAR_GAP = 2;
const WAVEFORM_WIDTH = BAR_COUNT * (BAR_WIDTH + BAR_GAP) - BAR_GAP; // 148px
const DOT_SIZE = 12;
const AVATAR_SIZE = 45;

interface VoiceMessagePlayerProps {
    message: any;
    /** Resolved audio URL (mediaData.url / mediaUrl). */
    url: string;
}

/**
 * WhatsApp-style voice message player: [avatar + mic overlay] [play/pause]
 * [pseudo-waveform + progress dot], with the elapsed/total duration under the
 * waveform's start side. The waveform is deterministic per message id (no audio
 * analysis) and stays LTR regardless of UI direction, like WhatsApp.
 */
const VoiceMessagePlayer: React.FC<VoiceMessagePlayerProps> = ({ message, url }) => {
    const { t } = useTranslation();
    const isDarkMode = useAppSelector(selectIsDarkMode);
    const waSessionPhoneNumber = useAppSelector(
        (state) => state.TeamInboxReducer?.waSessionPhoneNumber ?? null,
    );

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const waveformRef = useRef<HTMLDivElement | null>(null);
    const draggingRef = useRef(false);

    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    // Sticky "has been listened to" flag — WA greys the mic once played, and
    // 'ended' resets currentTime to 0, so a separate flag keeps it grey.
    const [hasEnded, setHasEnded] = useState(false);
    // Playback speed pill (1× / 1.5× / 2×), WA-style.
    const [playbackRate, setPlaybackRate] = useState(1);
    // Prefer real metadata; fall back to the message's declared ptt duration.
    const [duration, setDuration] = useState<number | null>(
        typeof message?.duration === 'number' || typeof message?.duration === 'string'
            ? Number(message.duration) || null
            : null,
    );

    const heights = useMemo(
        () => generateWaveformHeights(message?._id ?? url, BAR_COUNT),
        [message?._id, url],
    );

    // One HTMLAudioElement per player instance, rebuilt if the URL changes.
    useEffect(() => {
        const audio = new Audio(url);
        audio.preload = 'metadata';
        audioRef.current = audio;

        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onLoadedMetadata = () => {
            if (Number.isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration);
            }
        };
        const onEnded = () => {
            setPlaying(false);
            setCurrentTime(0);
            setHasEnded(true);
            audio.currentTime = 0;
        };
        const onPause = () => setPlaying(false);
        const onPlay = () => { setPlaying(true); setHasEnded(false); };

        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('play', onPlay);

        return () => {
            audio.pause();
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('play', onPlay);
            audioRef.current = null;
        };
    }, [url]);

    // Keep the live element's rate in sync with the pill.
    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = playbackRate;
    }, [playbackRate]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            audio.playbackRate = playbackRate;
            audio.play().catch((err) => console.warn('[team-inbox] Voice play failed', err));
        } else {
            audio.pause();
        }
    }, [playbackRate]);

    const cyclePlaybackRate = useCallback(() => {
        setPlaybackRate((prev) => (prev === 1 ? 1.5 : prev === 1.5 ? 2 : 1));
    }, []);

    // Seek from a pointer position over the waveform (LTR timeline).
    const seekFromPointer = useCallback((clientX: number) => {
        const el = waveformRef.current;
        const audio = audioRef.current;
        if (!el || !audio) return;
        const rect = el.getBoundingClientRect();
        const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        const total = (Number.isFinite(audio.duration) && audio.duration > 0)
            ? audio.duration
            : (duration ?? 0);
        if (total > 0) {
            audio.currentTime = fraction * total;
            setCurrentTime(fraction * total);
        }
    }, [duration]);

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        draggingRef.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        seekFromPointer(e.clientX);
    }, [seekFromPointer]);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (draggingRef.current) seekFromPointer(e.clientX);
    }, [seekFromPointer]);

    const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        draggingRef.current = false;
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    }, []);

    // Colors (WA palette).
    const accent = 'var(--wa-accent, #00a884)';
    const barColor = isDarkMode ? '#445056' : '#cbd2d7';
    const playedColor = isDarkMode ? '#6f7c85' : '#858f97';
    const iconColor = isDarkMode ? '#aebac1' : '#54656f';

    const total = duration ?? 0;
    const progress = total > 0 ? Math.min(1, currentTime / total) : 0;
    const playedBars = Math.round(progress * BAR_COUNT);
    // Active = currently playing or partway through; played = has been listened to.
    const active = playing || currentTime > 0;
    const played = currentTime > 0 || hasEnded;
    // Accent-tint the play control + progress dot while active/played; idle neutral.
    const playIconColor = active ? accent : iconColor;
    const dotColor = active ? accent : playedColor;
    // Mic disc: accent while unplayed, grey once listened to.
    const micDiscColor = played ? (isDarkMode ? '#6f7c85' : '#8696a0') : accent;
    // Elapsed while playing/scrubbed, total otherwise (WA behavior).
    const timeLabel = active
        ? formatDuration(Math.floor(currentTime))
        : (total > 0 ? formatDuration(Math.floor(total)) : '');

    // Avatar identity: own avatar for sent notes (session phone), sender for
    // received (group author, else the 1:1 peer). Skipped gracefully if unknown.
    const avatarContactId: string | undefined = message?.fromMe
        ? (waSessionPhoneNumber ? `${waSessionPhoneNumber}@c.us` : undefined)
        : (message?.author?._serialized ?? message?.author ?? message?.from ?? message?.chatId);

    return (
        // WA keeps the voice timeline LTR even in RTL locales.
        <Box dir="ltr" sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '2px' }}>
            {/* Avatar with mic overlay */}
            <Box sx={{ position: 'relative', width: AVATAR_SIZE, height: AVATAR_SIZE, flexShrink: 0 }}>
                {avatarContactId ? (
                    <ContactAvatar contactId={avatarContactId} size={AVATAR_SIZE} textSize={16} />
                ) : (
                    <Box sx={{
                        width: AVATAR_SIZE,
                        height: AVATAR_SIZE,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isDarkMode ? '#2a3942' : '#dfe5e7',
                    }}>
                        <MicIcon sx={{ fontSize: 24, color: iconColor }} />
                    </Box>
                )}
                {avatarContactId && (
                    /* Circular mic badge: accent while unplayed, grey once listened. */
                    <Box sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -4,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: micDiscColor,
                        boxShadow: '0 0 0 2px var(--whatsapp-bubble-received, #fff)',
                    }}>
                        <MicIcon sx={{ fontSize: 11, color: '#fff' }} />
                    </Box>
                )}
            </Box>

            {/* Play / pause */}
            <IconButton
                onClick={togglePlay}
                sx={{ width: 34, height: 34, padding: 0, color: playIconColor, flexShrink: 0 }}
                aria-label={playing ? 'Pause voice message' : 'Play voice message'}
            >
                {playing ? <PauseIcon sx={{ fontSize: 34 }} /> : <PlayArrowIcon sx={{ fontSize: 34 }} />}
            </IconButton>

            {/* Waveform + time — fixed to the avatar height so the waveform bars
                sit on the SAME vertical center line as the avatar and play control
                (WA-style). The duration/speed row hangs just beneath the waveform,
                absolutely positioned so it doesn't add column height and lift the
                waveform above the row's center. */}
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: AVATAR_SIZE }}>
                <Box
                    ref={waveformRef}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    sx={{
                        position: 'relative',
                        width: WAVEFORM_WIDTH,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        gap: `${BAR_GAP}px`,
                        cursor: 'pointer',
                        touchAction: 'none',
                    }}
                >
                    {heights.map((h, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: BAR_WIDTH,
                                height: h,
                                borderRadius: '2px',
                                backgroundColor: i < playedBars ? playedColor : barColor,
                                flexShrink: 0,
                            }}
                        />
                    ))}
                    {/* Progress dot */}
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: `calc(${progress * 100}% - ${DOT_SIZE / 2}px)`,
                        transform: 'translateY(-50%)',
                        width: DOT_SIZE,
                        height: DOT_SIZE,
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                        pointerEvents: 'none',
                    }} />
                </Box>
                <Box sx={{ position: 'absolute', top: 'calc(50% + 14px)', left: 0, display: 'flex', alignItems: 'center', gap: '8px', minHeight: '18px' }}>
                    <Box sx={{
                        fontSize: '11px',
                        lineHeight: '15px',
                        color: 'var(--wa-meta-text, #667781)',
                    }}>
                        {timeLabel}
                    </Box>
                    {/* Playback-speed pill — only once playback has started (mirrors
                        the timeLabel-elapsed gate). */}
                    {active && (
                        <Box
                            component="button"
                            onClick={cyclePlaybackRate}
                            sx={{
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                fontSize: '11px',
                                fontWeight: 600,
                                lineHeight: 1,
                                padding: '2px 6px',
                                borderRadius: '10px',
                                color: iconColor,
                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                                '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)' },
                            }}
                            aria-label={t('teamInbox.playbackSpeed', 'Playback speed')}
                        >
                            {playbackRate}×
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default React.memo(VoiceMessagePlayer);
